import React, { useMemo } from 'react';
import { useAtom } from 'jotai';
import { View, Text, Pressable } from '../../lib/react-native-compat';
import { jobOpenCommitmentsAtom } from '../../store/company';
import { fieldReportAtom, fieldReportDirtyAtom } from './state';
import AutoGrowTextarea from './AutoGrowTextarea';

// Quick Report subcontractors (bottom of the Equipment tab): one card per sub on
// site — name, headcount, hours, % complete and what they worked on. Cards
// edit the same 'labor' section rows the full report's LaborMatrix uses
// (group: 'sub'), so save / hydrate / review / sheet are unchanged.
//
// The name box suggests vendors from the job's open sub POs (cost types
// 54100/54200/54300 — the same set the Subcontractors tab pages on); free text
// is still allowed.
//
// When the row matches an open sub PO, a detail line under the name shows the
// PO's scope (largest-dollar line description — rate-type descriptions like
// "Standard Time" are skipped, they're billing terms, not scope), its date
// window (rental dates across lines, promised-ship as the finish fallback) and
// how much of the PO has been invoiced — plus an on-track status that compares
// the % complete the foreman enters against how much of the PO window has
// elapsed by the report date. A window that's already over flags the PO
// instead ("PO window ended…"), since a sub still on site past their PO dates
// is exactly what the office needs to hear about.

const SUB_COST_TYPES = new Set(['54100', '54200', '54300']);

const num = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : 0;
};
const round1 = (v) => Math.round(v * 10) / 10;

const lineAmt = (r) => {
    for (const k of ['Original_Line_Amount', 'PO_Ordered_Amount', 'PO_Open_Amount']) {
        const n = parseFloat(r?.[k]);
        if (Number.isFinite(n) && n !== 0) return Math.abs(n);
    }
    return 0;
};

// '2026-07-18' → '7/18'; window → '7/25 – 9/12'.
const shortDate = (iso) => (iso ? `${parseInt(iso.slice(5, 7), 10)}/${parseInt(iso.slice(8, 10), 10)}` : '');
const isoDay = (v) => (v ? String(v).slice(0, 10) : '');

// JDE billing-rate line descriptions that say nothing about scope.
const RATE_DESC = /^(st|ot|dt|standard time|straight time|reg(ular)? time|over ?time|double time|per ?diem|labor|time|mob(ilization)?|demob(ilization)?)$/i;

// % of the PO's date window elapsed as of the report date; null without a real
// window to measure against.
const windowUsed = (d, today) => {
    if (!d?.start || !d?.end || !(d.start < d.end) || !today) return null;
    const t = Date.parse(today);
    const s = Date.parse(d.start);
    const e = Date.parse(d.end);
    if (![t, s, e].every(Number.isFinite)) return null;
    return Math.min(100, Math.max(0, Math.round(((t - s) / (e - s)) * 100)));
};

const inputBase = {
    minHeight: 42,
    fontSize: 15,
    border: '1px solid #CBD5E1',
    borderRadius: 8,
    padding: '8px 10px',
    boxSizing: 'border-box',
    backgroundColor: '#fff',
    fontFamily: 'inherit',
};

const miniLabel = {
    fontSize: 10,
    fontWeight: '700',
    color: '#64748B',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    whiteSpace: 'nowrap',
};

const SubCards = ({ readOnly }) => {
    const [report, setReport] = useAtom(fieldReportAtom);
    const [, setDirty] = useAtom(fieldReportDirtyAtom);
    const [openCommitments] = useAtom(jobOpenCommitmentsAtom);

    // Vendor / PO suggestions from the job's open sub commitments.
    const subVendors = useMemo(() => {
        const byVendor = new Map();
        (openCommitments || []).forEach((r) => {
            if (!SUB_COST_TYPES.has(String(r?.Cost_Type_Code ?? '').trim())) return;
            const vendor = String(r?.Supplier_AB_Name ?? '').trim();
            if (!vendor) return;
            const po = String(r?.PO_Number ?? '').trim();
            if (!byVendor.has(vendor)) byVendor.set(vendor, po);
        });
        return [...byVendor.entries()].sort((a, b) => a[0].localeCompare(b[0]));
    }, [openCommitments]);

    // PO # → { desc, start, end, invoicedPct } aggregated across that PO's sub
    // lines: scope from the largest-dollar line, window from rental dates
    // (promised-ship covers a missing finish), invoiced % from ordered vs open.
    const detailByPO = useMemo(() => {
        const m = new Map();
        (openCommitments || []).forEach((r) => {
            if (!SUB_COST_TYPES.has(String(r?.Cost_Type_Code ?? '').trim())) return;
            const po = String(r?.PO_Number ?? '').trim();
            if (!po) return;
            if (!m.has(po)) m.set(po, { desc: '', descAmt: -1, start: '', end: '', ordered: 0, open: 0 });
            const d = m.get(po);
            const a = lineAmt(r);
            const desc = [r?.PO_Line_Description, r?.PO_Line_Description_2]
                .map((v) => String(v ?? '').trim()).filter(Boolean).join(' ');
            if (desc && !RATE_DESC.test(desc) && a > d.descAmt) {
                d.descAmt = a;
                d.desc = desc;
            }
            const st = isoDay(r?.Rental_Start_Date);
            const en = isoDay(r?.Rental_End_Date) || isoDay(r?.Promised_Shipment_Date);
            if (st && (!d.start || st < d.start)) d.start = st;
            if (en && (!d.end || en > d.end)) d.end = en;
            d.ordered += num(r?.PO_Ordered_Amount);
            d.open += num(r?.PO_Open_Amount);
        });
        m.forEach((d) => {
            d.invoicedPct = d.ordered > 0
                ? Math.min(100, Math.max(0, Math.round(((d.ordered - d.open) / d.ordered) * 100)))
                : null;
        });
        return m;
    }, [openCommitments]);

    // Vendor name → PO #, matched case/space-insensitively so a typed name
    // still picks up its PO.
    const poByVendor = useMemo(() => {
        const m = new Map();
        subVendors.forEach(([vendor, po]) => {
            if (po) m.set(vendor.trim().toLowerCase(), po);
        });
        return m;
    }, [subVendors]);

    if (!report) return null;

    const rows = report.sections?.labor || [];
    const subs = rows.map((r, i) => ({ r, i })).filter(({ r }) => r.group === 'sub');

    const setRows = (next) => {
        setReport({ ...report, sections: { ...report.sections, labor: next } });
        setDirty(true);
    };
    const updateRow = (i, patch) => setRows(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

    // Typing / picking a name pulls that vendor's PO # and date window onto the
    // row (and drops them again when the name no longer matches an open sub PO).
    const setName = (i, name) => {
        const po = poByVendor.get(String(name).trim().toLowerCase()) || '';
        const d = po ? detailByPO.get(po) : null;
        updateRow(i, { name, poNumber: po, poStart: d?.start || '', poEnd: d?.end || '' });
    };

    // The PO stored on the row, else looked up from the open sub POs by name
    // (covers rows saved before the PO was carried on the row).
    const poOf = (r) => String(r.poNumber || '').trim() || poByVendor.get(String(r.name || '').trim().toLowerCase()) || '';

    // Detail line under the name: scope · dates · % invoiced. The date part is
    // left off when the window is already over — the status line flags that
    // case instead.
    const detailOf = (r) => {
        const d = detailByPO.get(poOf(r));
        if (!d) return '';
        const today = report.reportDate || '';
        const parts = [];
        if (d.desc) parts.push(d.desc);
        const past = d.end && today && d.end < today;
        if (!past) {
            if (d.start && d.end && d.start !== d.end) parts.push(`On site ${shortDate(d.start)} – ${shortDate(d.end)}`);
            else if (d.end) parts.push(`Finishes ${shortDate(d.end)}`);
            else if (d.start) parts.push(`Starts ${shortDate(d.start)}`);
        }
        if (d.invoicedPct != null) parts.push(d.invoicedPct >= 100 ? 'PO fully invoiced' : `${d.invoicedPct}% of PO invoiced`);
        return parts.join(' · ');
    };

    // On-track status: the foreman's % complete vs how much of the PO window
    // has elapsed. More than 10 points behind reads as a warning; a window
    // that's already over is flagged regardless.
    const statusOf = (r) => {
        const d = detailByPO.get(poOf(r));
        if (!d) return null;
        const today = report.reportDate || '';
        if (d.end && today && d.end < today) {
            return { text: `PO window ended ${shortDate(d.end)} — still on site?`, tone: 'warn' };
        }
        const used = windowUsed(d, today);
        if (used == null) return null;
        const pc = parseFloat(r.percentComplete);
        if (Number.isFinite(pc)) {
            const diff = pc - used;
            if (diff < -10) return { text: `Behind — ${Math.round(pc)}% complete with ${used}% of PO time used`, tone: 'warn' };
            if (diff > 10) return { text: `Ahead — ${Math.round(pc)}% complete with ${used}% of PO time used`, tone: 'ok' };
            return { text: `On track — ${Math.round(pc)}% complete, ${used}% of PO time used`, tone: 'ok' };
        }
        return { text: `${used}% of PO time used`, tone: null };
    };
    const addSub = () => setRows([...rows, { group: 'sub', name: '', headcount: '', hoursWorked: '' }]);
    const delRow = (i) => setRows(rows.filter((_, idx) => idx !== i));

    const totalHeadcount = subs.reduce((t, { r }) => t + num(r.headcount), 0);
    const totalMH = round1(subs.reduce((t, { r }) => t + num(r.headcount) * num(r.hoursWorked), 0));

    const numField = (label, i, r, name, placeholder) => (
        <View style={{ width: 64, flexShrink: 0 }}>
            <Text style={miniLabel}>{label}</Text>
            <input
                type="number"
                inputMode="decimal"
                value={r[name] ?? ''}
                disabled={readOnly}
                placeholder={placeholder}
                onChange={(e) => updateRow(i, { [name]: e.target.value })}
                style={{ ...inputBase, width: '100%', textAlign: 'right', marginTop: 3, ...(readOnly ? { backgroundColor: '#F1F5F9' } : null) }}
            />
        </View>
    );

    return (
        <View>
            <datalist id="fr-subs">
                {subVendors.map(([vendor, po]) => {
                    const d = po ? detailByPO.get(po) : null;
                    const hint = [po ? `PO ${po}` : '', d?.end ? `thru ${shortDate(d.end)}` : '']
                        .filter(Boolean).join(' · ');
                    return <option key={vendor} value={vendor}>{hint}</option>;
                })}
            </datalist>

            {subs.length === 0 && (
                <View style={{ borderRadius: 10, border: '1px dashed #CBD5E1', backgroundColor: '#F8FAFC', padding: 16, alignItems: 'center', marginBottom: 10 }}>
                    <Text style={{ fontSize: 13, color: '#64748B', textAlign: 'center' }}>
                        {readOnly ? 'No subcontractors on this report.' : 'No subs on site today — add one if a sub worked.'}
                    </Text>
                </View>
            )}

            {subs.map(({ r, i }) => (
                <View key={i} style={{ borderRadius: 12, border: '1px solid #E2E8F0', backgroundColor: '#fff', padding: 12, marginBottom: 10 }}>
                    {/* Name + the three numbers ride on one line; they wrap only
                        when the card is too narrow to hold them. */}
                    <View style={{ flexDirection: 'row', alignItems: 'flex-end', flexWrap: 'wrap', columnGap: 6, rowGap: 6 }}>
                        <View style={{ flex: 1, minWidth: 96 }}>
                            {/* PO # rides on the label line so it's visible without
                                opening the vendor suggestions. */}
                            <View style={{ flexDirection: 'row', alignItems: 'baseline', columnGap: 6 }}>
                                <Text style={miniLabel}>Subcontractor</Text>
                                {poOf(r) ? (
                                    <Text style={{ ...miniLabel, color: '#94A3B8', fontWeight: '600', letterSpacing: 0.2, textTransform: 'none' }}>
                                        {`PO ${poOf(r)}`}
                                    </Text>
                                ) : null}
                            </View>
                            <input
                                type="text"
                                list="fr-subs"
                                value={r.name || ''}
                                disabled={readOnly}
                                placeholder="Subcontractor"
                                onChange={(e) => setName(i, e.target.value)}
                                style={{ ...inputBase, width: '100%', fontWeight: 600, marginTop: 3, ...(readOnly ? { backgroundColor: '#F1F5F9' } : null) }}
                            />
                        </View>
                        {numField('Headcount', i, r, 'headcount', '0')}
                        {numField('Hours', i, r, 'hoursWorked', '0')}
                        {numField('% Complete', i, r, 'percentComplete', '%')}
                        {!readOnly && (
                            <Pressable onPress={() => delRow(i)} style={{ minHeight: 42, minWidth: 26, alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}>
                                <Text style={{ fontSize: 14, color: '#DC2626' }}>✕</Text>
                            </Pressable>
                        )}
                    </View>
                    {(() => {
                        const det = detailOf(r);
                        const st = statusOf(r);
                        if (!det && !st) return null;
                        const toneColor = st?.tone === 'warn' ? '#B45309' : st?.tone === 'ok' ? '#15803D' : null;
                        return (
                            <Text style={{ fontSize: 11.5, color: '#64748B', marginTop: 6, lineHeight: '16px' }}>
                                {det}
                                {st ? (
                                    <Text style={{ ...(toneColor ? { color: toneColor, fontWeight: '600' } : null) }}>
                                        {`${det ? ' · ' : ''}${st.text}`}
                                    </Text>
                                ) : null}
                            </Text>
                        );
                    })()}
                    <AutoGrowTextarea
                        value={r.notes || ''}
                        disabled={readOnly}
                        placeholder="What did this sub work on today?"
                        onChange={(e) => updateRow(i, { notes: e.target.value })}
                        minHeight={44}
                        style={{ ...inputBase, width: '100%', marginTop: 8, ...(readOnly ? { backgroundColor: '#F1F5F9' } : null) }}
                    />
                </View>
            ))}

            <View style={{ flexDirection: 'row', alignItems: 'center', columnGap: 12 }}>
                {!readOnly && (
                    <Pressable onPress={addSub} style={{ minHeight: 32, justifyContent: 'center', cursor: 'pointer' }}>
                        <Text style={{ fontSize: 12, fontWeight: '600', color: '#1D4ED8' }}>+ Add sub</Text>
                    </Pressable>
                )}
                {subs.length > 0 && (
                    <Text style={{ flex: 1, textAlign: 'right', fontSize: 12, fontWeight: '700', color: '#0F172A' }}>
                        {`${totalHeadcount} on site · ${totalMH} MH`}
                    </Text>
                )}
            </View>
        </View>
    );
};

export default SubCards;
